import { z } from "zod";
import { Rng } from "../rng.js";
import type { GameModule, GeneratedVariant, Hint, SettingsField, ValidationResult } from "../types.js";

// Двоичный замок: на табло двери число, игрок набирает его лампочками-разрядами.
// Обратный режим: горят лампы, игрок вводит число.

export const binaryLockSettingsSchema = z.object({
  bits: z.number().int().min(3).max(8).default(4),
  min: z.number().int().min(0).max(255).default(1),
  max: z.number().int().min(1).max(255).default(15),
  rounds: z.number().int().min(1).max(10).default(1),
  direction: z.enum(["dec_to_bin", "bin_to_dec"]).default("dec_to_bin"),
  showWeights: z.boolean().default(true),
  showPartial: z.boolean().default(true),
  hintMode: z.enum(["one_bit", "high_bit"]).default("one_bit"),
});

export type BinaryLockSettings = z.infer<typeof binaryLockSettingsSchema>;

export const binaryLockPayloadSchema = z.object({
  bits: z.number().int(),
  direction: z.enum(["dec_to_bin", "bin_to_dec"]),
  showWeights: z.boolean(),
  showPartial: z.boolean(),
  targets: z.array(z.number().int()),
});

export type BinaryLockPayload = z.infer<typeof binaryLockPayloadSchema>;

export const binaryLockSolutionSchema = z.object({
  values: z.array(z.number().int()),
});

export type BinaryLockSolution = z.infer<typeof binaryLockSolutionSchema>;

const FIELDS: SettingsField[] = [
  { path: "bits", label: "Разрядов", kind: "number", min: 3, max: 8, group: "Замок" },
  { path: "min", label: "Число от", kind: "number", min: 0, max: 255, group: "Замок" },
  { path: "max", label: "Число до", kind: "number", min: 1, max: 255, group: "Замок" },
  { path: "rounds", label: "Чисел подряд", kind: "number", min: 1, max: 10, group: "Замок" },
  {
    path: "direction",
    label: "Направление",
    kind: "select",
    options: [
      { value: "dec_to_bin", label: "Показано число — набрать биты" },
      { value: "bin_to_dec", label: "Показаны биты — ввести число" },
    ],
    group: "Замок",
  },
  { path: "showWeights", label: "Подписи 8/4/2/1", kind: "boolean", group: "Помощь игроку" },
  { path: "showPartial", label: "Показывать набранное значение", kind: "boolean", group: "Помощь игроку" },
  {
    path: "hintMode",
    label: "Что открывает подсказка",
    kind: "select",
    options: [
      { value: "one_bit", label: "Один разряд" },
      { value: "high_bit", label: "Старший горящий разряд" },
    ],
    group: "Помощь игроку",
  },
];

const popcount = (value: number): number => {
  let n = value;
  let count = 0;
  while (n) {
    count += n & 1;
    n >>= 1;
  }
  return count;
};

function generate(settings: BinaryLockSettings, seed: number): GeneratedVariant<BinaryLockPayload, BinaryLockSolution> {
  const rng = new Rng(seed);
  const ceiling = Math.min(settings.max, (1 << settings.bits) - 1);
  const floor = Math.min(settings.min, ceiling);

  const targets: number[] = [];
  for (let i = 0; i < settings.rounds; i++) {
    let value = rng.int(floor, ceiling);
    // два одинаковых числа подряд выглядят как сбой, а не как новый раунд
    if (i > 0 && value === targets[i - 1] && ceiling > floor) {
      value = value === ceiling ? floor : value + 1;
    }
    targets.push(value);
  }

  return {
    payload: {
      bits: settings.bits,
      direction: settings.direction,
      showWeights: settings.showWeights,
      showPartial: settings.showPartial,
      targets,
    },
    solution: { values: targets },
    metrics: {
      rounds: targets.length,
      bitsToToggle: targets.reduce((sum, t) => sum + popcount(t), 0),
    },
  };
}

function validate(
  payload: BinaryLockPayload,
  submitted: BinaryLockSolution,
  _settings: BinaryLockSettings,
): ValidationResult {
  const values = submitted.values ?? [];
  if (values.length !== payload.targets.length) {
    return { solved: false, reason: "rounds_mismatch", mistakes: 0 };
  }
  const wrong = payload.targets.filter((target, i) => values[i] !== target);
  return {
    solved: wrong.length === 0,
    reason: wrong.length ? "wrong_value" : undefined,
    mistakes: wrong.length,
    details: { wrongRounds: wrong.length },
  };
}

function minDurationMs(payload: BinaryLockPayload): number {
  // минимум — успеть нажать нужные лампы: примерно 120 мс на нажатие
  const presses = payload.targets.reduce((sum, t) => sum + popcount(t), 0);
  return Math.max(700, presses * 120);
}

function hint(payload: BinaryLockPayload, solution: BinaryLockSolution, used: number): Hint | null {
  const bits = payload.bits;
  const round = Math.min(Math.floor(used / bits), payload.targets.length - 1);
  const target = solution.values[round] ?? 0;

  const highest = target === 0 ? 0 : Math.floor(Math.log2(target));
  const position = bits - 1 - (used % bits);
  const bit = payload.direction === "bin_to_dec" || used === 0 ? highest : position;
  const weight = 1 << bit;
  const on = Boolean(target & weight);

  return {
    kind: "bit",
    data: { round, bit, on },
    text: on
      ? `Разряд ${weight} должен гореть`
      : `Разряд ${weight} гореть не должен`,
  };
}

export const binaryLock: GameModule<BinaryLockSettings, BinaryLockPayload, BinaryLockSolution> = {
  code: "binary_lock",
  title: "Двоичный замок",
  description: "Набрать число на двери лампочками-разрядами",
  settingsSchema: binaryLockSettingsSchema,
  payloadSchema: binaryLockPayloadSchema,
  solutionSchema: binaryLockSolutionSchema,
  defaultSettings: binaryLockSettingsSchema.parse({}),
  settingsFields: FIELDS,
  generate,
  validate,
  minDurationMs,
  hint,
};
